import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@busy/api';

type AuthState = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /** True for the brief window between SIGNED_IN auth event and the
   *  splash dismissing. App.tsx renders the post-login quote splash
   *  while this is true. Cleared by clearJustSignedIn() once dismissed. */
  justSignedIn: boolean;
  init: () => () => void;
  signOut: () => Promise<void>;
  clearJustSignedIn: () => void;
};

export const useAuth = create<AuthState>((set) => ({
  session: null,
  user: null,
  loading: true,
  justSignedIn: false,
  init: () => {
    supabase.auth.getSession().then(({ data }) => {
      set({ session: data.session, user: data.session?.user ?? null, loading: false });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // Only set justSignedIn for the actual sign-in submission — not for
      // INITIAL_SESSION (session restore on app boot) or USER_UPDATED.
      // Showing the splash on every page reload would be obnoxious.
      const isFreshSignIn = event === 'SIGNED_IN';
      set((s) => ({
        session,
        user: session?.user ?? null,
        loading: false,
        justSignedIn: isFreshSignIn ? true : s.justSignedIn,
      }));
    });
    return () => sub.subscription.unsubscribe();
  },
  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, user: null, justSignedIn: false });
  },
  clearJustSignedIn: () => set({ justSignedIn: false }),
}));
